import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Search, Copy, ImageIcon, FileText as FilePdf } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const MediaLibrary = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (kind) params.kind = kind;
      const res = await axios.get(`${API}/cms/media`, { params });
      setItems(res.data);
    } catch {
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [q, kind]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/cms/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file(s) uploaded`);
      e.target.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this file? Any page using its URL will show a broken image.")) return;
    await axios.delete(`${API}/cms/media/${id}`);
    toast.success("Deleted");
    load();
  };

  const copyUrl = (url) => {
    const full = `${BASE}${url}`;
    navigator.clipboard.writeText(full);
    toast.success("URL copied — paste into any image field");
  };

  const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(2)} MB`);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Media Library</h1>
          <p className="text-sm text-slate-500">Upload images and PDFs. Paste the URL anywhere on the site.</p>
        </div>
        <div>
          <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" onChange={onUpload} className="hidden" data-testid="media-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-prepify-600 hover:bg-prepify-700 text-white font-medium px-5 py-2.5 rounded-lg disabled:opacity-50" data-testid="media-upload-btn">
            <Upload size={16} /> {uploading ? "Uploading…" : "Upload files"}
          </button>
        </div>
      </header>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by filename or alt text…" className="w-full border border-slate-200 rounded-lg pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" />
        </div>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
          <option value="">All files</option>
          <option value="image">Images</option>
          <option value="pdf">PDFs</option>
        </select>
      </div>

      {loading ? (
        <div className="text-slate-400 text-center py-10">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-400 text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">
          No files yet. Click <b>Upload files</b> to add images or PDFs.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {items.map((it) => (
            <div key={it.id} className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition" data-testid={`media-${it.id}`}>
              <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
                {it.kind === "image" ? (
                  <img src={`${BASE}${it.url}`} alt={it.alt_text || it.filename} className="w-full h-full object-cover" />
                ) : it.kind === "pdf" ? (
                  <div className="flex flex-col items-center text-red-500"><FilePdf size={44} strokeWidth={1.5} /><span className="text-xs mt-1">PDF</span></div>
                ) : (
                  <ImageIcon size={44} className="text-slate-300" />
                )}
              </div>
              <div className="p-3 space-y-1.5">
                <p className="text-xs font-medium text-slate-800 truncate" title={it.filename}>{it.filename}</p>
                <p className="text-[11px] text-slate-400">{fmtSize(it.size)} • {new Date(it.created_at).toLocaleDateString()}</p>
                <div className="flex gap-1 pt-1">
                  <button onClick={() => copyUrl(it.url)} className="flex-1 text-xs inline-flex items-center justify-center gap-1 py-1.5 border border-slate-200 rounded hover:bg-prepify-50 hover:text-prepify-700 hover:border-prepify-200 transition"><Copy size={12} /> Copy URL</button>
                  <button onClick={() => onDelete(it.id)} className="text-xs inline-flex items-center justify-center p-1.5 border border-slate-200 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition" data-testid={`delete-${it.id}`}><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
